/* eslint-disable import/no-deprecated */
import { useFocusVisible, useHover } from '@fullstory/react-hooks';
import { classNames } from '@fullstory/util/fmt';
import React, { ComponentProps, ReactNode } from 'react';
import { Box } from '../../legacy/Box';
import { Flex } from '../../legacy/Flex';
import { identifyComponent } from '../../util';
import { Text } from '../Text';
import css from './index.module.scss';

// Note: label was originally of type string but the type was broadened to allow all ReactNode types. The type parameter T
// allows the original uses to narrow the type back to string, if desired, with Option<string>.
export interface Option<T extends ReactNode = ReactNode> {
  id: string;
  label: T;
  description?: string;
}

interface RadioOptionProps<T extends Option> {
  option: T;
  name: string;
  checked: boolean;
  onChange: (value: T) => void;
  disabled?: boolean;
}

export function RadioOption<T extends Option>(props: RadioOptionProps<T>): React.ReactElement {
  const { checked, onChange, option, name, disabled = false, ...rest } = props;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => onChange(option);
  const { hovered, hoverProps } = useHover();
  const { focusVisible, ...focusProps } = useFocusVisible();
  let labelColor: ComponentProps<typeof Text>['color'] = 'default';
  let descriptionColor: ComponentProps<typeof Text>['color'] = 'secondary';
  if (disabled) {
    labelColor = 'disabled';
    descriptionColor = 'disabled';
  } else if (hovered || focusVisible) {
    labelColor = 'emphasized';
  }

  const id = `${name}-${option.id}`;

  return (
    <Flex
      data-component="RadioOption"
      className={classNames(css.wrapper, { [css.focused]: focusVisible })}
      alignItems="flex-start"
      {...hoverProps}
      {...rest}
      {...identifyComponent('radio-option', rest)}
    >
      <Box paddingRight={8} className={css.container}>
        <input
          disabled={disabled}
          name={name}
          id={id}
          data-testid={id}
          type="radio"
          checked={checked}
          onChange={handleChange}
          {...focusProps}
        />
        <span
          className={classNames(css.border, { [css.hovered]: hovered, [css.disabled]: disabled })}
        >
          <span className={classNames(css.radio, { [css.checked]: checked })} />
        </span>
      </Box>
      <Flex column alignItems="flex-start">
        <label htmlFor={id}>
          <Text tag="span" appearance="bodyMedium" color={labelColor}>
            {option.label}
          </Text>
        </label>
        <Text tag="span" appearance="bodyMedium" color={descriptionColor}>
          {option.description}
        </Text>
      </Flex>
    </Flex>
  );
}
